import { ManagePacketService } from 'services/ManagePacketService';
import { typeMap } from './typeMap';
import { SetStateAction } from 'react';
import { formattingAction } from 'shared/helpers/FormatAction';
import DateFnsAdapter from '@date-io/date-fns';
import { CanceledError, AxiosError } from 'axios';

export const abortProtocol: { protocol: AbortController | null } = {
    protocol: null,
};

/**
 * Fetch data based on the offset and limit.
 *
 * @param {number} offset - Offset value.
 * @param {number} limit - Limit value.
 * @returns {Promise<void>} A promise that resolves when the data is fetched.
 */
export const fetchPackets = async (
    searchByLoan: string,
    searchByBin: string,
    searchByClient: string,
    searchByOperator: string,
    searchByCM9: string,
    searchByuploadDate: string,
    searchBycompletedDate: string,
    SLADaysLeft: number,
    uploadAge: number,
    sort: string | SetStateAction<string>,
    offset: number,
    limit: number,
    packetStatus: any[],
    packetType: any[],
    callback: (
        mappedRows: any,
        totalRows: number,
        isNetworkError: boolean
    ) => void
): Promise<void> => {
    // console.log('Called fetchPackets');

    let isDone: boolean | null = false;
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types, prefer-const
    let isNetworkError: boolean = false;
    const dateFns = new DateFnsAdapter();
    const searchFromUploadDate = new Date(searchByuploadDate);
    const searchFromCompletedDate = new Date(searchBycompletedDate);

    const stringUploadDate =
        Number.isNaN(Date.parse(searchByuploadDate)) === false &&
        typeof searchByuploadDate !== 'number'
            ? dateFns.formatByString(searchFromUploadDate, 'yyyy-MM-dd')
            : null;
    const stringCompletedDate =
        Number.isNaN(Date.parse(searchBycompletedDate)) === false &&
        typeof searchBycompletedDate !== 'number'
            ? dateFns.formatByString(searchFromCompletedDate, 'yyyy-MM-dd')
            : null;

    try {
        const packetRespond = ManagePacketService.getPackets({
            operatorName:
                searchByOperator !== '' ? searchByOperator : undefined,
            binNumber: searchByBin !== '' ? searchByBin : undefined,
            clientName: searchByClient !== '' ? searchByClient : undefined,
            loanNumber: searchByLoan !== '' ? searchByLoan : undefined,
            cm9Number: searchByCM9 !== '' ? searchByCM9 : undefined,
            uploadDate:
                stringUploadDate !== null ? stringUploadDate : undefined,
            completedDate:
                stringCompletedDate !== null ? stringCompletedDate : undefined,
            slaDaysLeft: SLADaysLeft > 0 ? SLADaysLeft : undefined,
            uploadAge: uploadAge > 0 ? uploadAge : undefined,
            sort: sort !== '' ? sort : undefined,
            offset: offset > 0 ? offset : undefined,
            limit: limit > 0 ? limit : undefined,
            packetStatus:
                Array.isArray(packetStatus) && packetStatus.length > 0
                    ? packetStatus
                    : undefined,
            packetType:
                Array.isArray(packetType) && packetType.length > 0
                    ? packetType
                    : undefined,
        });
        abortProtocol.protocol = ManagePacketService.getAbortProtocol();
        const packetResponses = await packetRespond
            .then((result) => {
                if (result instanceof CanceledError) {
                    return null;
                }
                return result;
            })
            .catch((e) => {
                if (e instanceof CanceledError) {
                    return null;
                }
                return e;
            });

        if (packetResponses !== null && packetResponses.status === 200) {
            if (packetResponses.data.data[0].paginatedResult.length > 0) {
                const totalRows =
                    packetResponses.data.data[0].totalCount[0]?.count ?? 0;
                const mappedRows =
                    packetResponses.data.data[0].paginatedResult.map(
                        (item: any) => ({
                            actions: {
                                type:
                                    item.status === ''
                                        ? typeMap.notExpected?.[0]
                                        : typeMap[item.status]?.[0],
                                ...formattingAction(item),
                            },
                            bin: item.binNumber,
                            clientName: item.clientName,
                            completedDate: item.completedDate,
                            completedDateDifference:
                                item.completedDateDifference,
                            escalated: item.escalated,
                            escalatedDate: item.escalatedDate,
                            escalatedAtDifference: item.ecalatedAtDifference,
                            escalatedByRole: item.escalatedByRole,
                            loanNumber: item.loanNumber,
                            operatorId: item.createdBy,
                            cm9Number: item.cm9Number,
                            packetStatus:
                                item.status === ''
                                    ? typeMap.notExpected?.[1]
                                    : typeMap[item.status]?.[1],
                            packetType: item.type,
                            recordNo: item.uniqueRef,
                            reason: item.statusMessage,
                            archiveDateDifference: item.archiveDateDifference,
                            archiveTill: item.archiveTill,
                            SLADate: item.slaDate,
                            uploadedDate: item.createdAt,
                            documents: item.documents,
                            clientId: item.clientId,
                            createdAtDifference: item.createdAtDifference,
                            expiryDate: item.expiryDate,
                            expiryDateDifference: item.expiryDateDifference,
                            reportFile: item.reportFile,
                            reviewRequired: item.reviewRequired,
                            slaDateDifference: item.slaDateDifference,
                            updatedAtDifference: item.updatedAtDifference,
                            updatedAt: item.updatedAt,
                            file: item.file,
                            isOpen: item.operatorAction.open,
                            openedBy: item.operatorAction.openedBy,
                            enableAuditDownload: item.enableAuditDownload,
                            enableDocumentDownload: item.enableDocumentDownload,
                            highlighted: item.highlighted,
                            packetAuditFile: item.packetAuditFile,
                            packetAuditFileCSV: item.packetAuditFileCSV,
                            cm9Timer: item.cm9Timer,
                            preferredDocuments: item.preferredDocuments,
                            placeholder: item.placeholder,
                            updateCategory: item.updateCategory,
                            showCategory: item.showCategory,
                            documentsRequired: item.documentsRequired,
                            expectedMissingDocuments:
                                item.expectedMissingDocuments,
                            operatorName: item.operatorName,
                            allowGroupingUpdate: item.allowGroupingUpdate,
                            groupingFailed: item.groupingFailed,
                            totalGroups: item.totalGroups,
                            reportReviewed: item.reportReviewed,
                            reworkComments: item.reworkComments,
                        })
                    );
                isDone = true;
                callback(mappedRows, totalRows, isNetworkError);
                return;
            }
            isDone = true;
            callback([], 0, isNetworkError);
        } else if (packetResponses === null) {
            isDone = null;
        } else if (
            packetResponses !== null &&
            packetResponses instanceof AxiosError
        ) {
            if (packetResponses.code === 'ERR_NETWORK') {
                isNetworkError = true;
                throw new Error(packetResponses.message);
            }
        }
    } catch (e: any) {
        if (e.message === 'Network Error') {
            throw new Error('Network Error');
        }
    } finally {
        if (isDone === false) {
            callback([], 0, isNetworkError);
        }
    }
};
